import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateEntityRequest,
  UpdateEntityRequest,
  CreateEntityContactRequest,
  UpdateEntityContactRequest,
} from '@vagusx/clients';
import { getEntityClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook to search entities.
 */
export function useEntities(filters?: {
  type?: string;
  searchTerm?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: queryKeys.entities.list(filters),
    queryFn: () => getEntityClient().search(filters),
  });
}

/**
 * Hook to fetch a single entity by ID.
 */
export function useEntity(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.entities.detail(id ?? ''),
    queryFn: () => getEntityClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch contacts for an entity.
 */
export function useEntityContacts(entityId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.entities.contacts(entityId ?? ''),
    queryFn: () => getEntityClient().getContacts(entityId!),
    enabled: !!entityId,
  });
}

/**
 * Hook to create an entity.
 */
export function useCreateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEntityRequest) => getEntityClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.all });
    },
  });
}

/**
 * Hook to update an entity.
 */
export function useUpdateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEntityRequest }) =>
      getEntityClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.all });
    },
  });
}

/**
 * Hook to delete an entity.
 */
export function useDeleteEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getEntityClient().deleteEntity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.all });
    },
  });
}

/**
 * Hook to create an entity contact.
 */
export function useCreateEntityContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, data }: { entityId: string; data: CreateEntityContactRequest }) =>
      getEntityClient().createContact(entityId, data),
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.contacts(entityId) });
    },
  });
}

/**
 * Hook to update an entity contact.
 */
export function useUpdateEntityContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      entityId,
      contactId,
      data,
    }: {
      entityId: string;
      contactId: string;
      data: UpdateEntityContactRequest;
    }) => getEntityClient().updateContact(entityId, contactId, data),
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.contacts(entityId) });
    },
  });
}

/**
 * Hook to delete an entity contact.
 */
export function useDeleteEntityContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, contactId }: { entityId: string; contactId: string }) =>
      getEntityClient().deleteContact(entityId, contactId),
    onSuccess: (_data, { entityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.contacts(entityId) });
    },
  });
}
